import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { supabase } from '../services/supabase';
import { supabaseChatService } from '../services/supabaseChatService';

export interface KidsChurchChatUser {
  id: string;
  fullName: string;
  avatar?: string;
  unreadCount: number;
  lastMessage?: string;
  lastMessageTime?: string;
  isParent: boolean;
  children?: string[];
}

const fetchChatUsers = async (
  currentUserId: string,
  includeUserIds: string[] = []
): Promise<KidsChurchChatUser[]> => {
  // Re-implement minimal logic that mobile side uses
  const { data: parentData, error: parentError } = await supabase
    .from('kids_church_children')
    .select('parent_id, first_name, last_name')
    .not('parent_id', 'is', null);
  if (parentError) throw parentError;

  const { data: staffData, error: staffError } = await supabase
    .from('kids_church_staff')
    .select('user_id')
    .eq('is_active', true);
  if (staffError) throw staffError;

  const { data: teacherUsers, error: teacherUsersError } = await supabase
    .from('users')
    .select('id')
    .contains('roles', ['teacher']);
  if (teacherUsersError) throw teacherUsersError;

  const parentIds = Array.from(
    new Set(parentData?.map((c) => c.parent_id).filter(Boolean) as string[])
  );
  const staffIds = Array.from(
    new Set([
      ...(staffData ?? []).map((staff) => staff.user_id as string),
      ...(teacherUsers ?? []).map((user) => user.id as string),
    ])
  );
  const allUserIds = Array.from(
    new Set([...parentIds, ...staffIds, ...includeUserIds])
  ).filter((id) => id !== currentUserId);
  if (allUserIds.length === 0) return [];

  const { data: usersData, error: usersError } = await supabase
    .from('users')
    .select('id, full_name, avatar')
    .in('id', allUserIds);
  if (usersError) throw usersError;

  // map parent children
  const parentChildrenMap = new Map<string, string[]>();
  parentData?.forEach((child) => {
    if (child.parent_id) {
      const prev = parentChildrenMap.get(child.parent_id) || [];
      prev.push(`${child.first_name} ${child.last_name}`);
      parentChildrenMap.set(child.parent_id, prev);
    }
  });
  const result: KidsChurchChatUser[] = [];
  for (const user of usersData ?? []) {
    const unreadCount = await supabaseChatService.getUnreadMessageCountFromSender(
      currentUserId,
      user.id
    );

    const { data: lastMsg } = await supabase
      .from('messages')
      .select('content, created_at')
      .or(
        `and(sender_id.eq.${currentUserId},receiver_id.eq.${user.id}),and(sender_id.eq.${user.id},receiver_id.eq.${currentUserId})`
      )
      .is('hidden_at', null)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    result.push({
      id: user.id,
      fullName: user.full_name,
      avatar: user.avatar,
      unreadCount: unreadCount ?? 0,
      lastMessage: lastMsg?.content ?? undefined,
      lastMessageTime: lastMsg?.created_at ?? undefined,
      isParent: parentIds.includes(user.id),
      children: parentChildrenMap.get(user.id),
    });
  }

  return result.sort((a, b) => {
    if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
    if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
    if (a.lastMessageTime && b.lastMessageTime) {
      return new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime();
    }
    return 0;
  });
};

export const useKidsChurchChatUsers = (
  currentUserId: string,
  includeUserIds: string[] = []
) => {
  const queryClient = useQueryClient();
  const debounceRef = useRef<number | null>(null);
  const includeKey = includeUserIds.slice().sort().join(',');

  const query = useQuery({
    queryKey: ['kids-chat-users', currentUserId, includeKey],
    queryFn: () => fetchChatUsers(currentUserId, includeUserIds),
    enabled: !!currentUserId,
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (!currentUserId) return;
    const includeSet = includeUserIds.length > 0 ? new Set(includeUserIds) : null;
    const scheduleInvalidate = () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['kids-chat-users', currentUserId],
          exact: false,
        });
      }, 300);
    };

    const channel = supabase
      .channel(`kids-chat-users-${currentUserId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        (payload) => {
          const message = payload.new as { sender_id: string; receiver_id: string };
          const otherUserId =
            message.sender_id === currentUserId ? message.receiver_id : message.sender_id;
          if (
            message.sender_id === currentUserId ||
            message.receiver_id === currentUserId
          ) {
            if (!includeSet || includeSet.has(otherUserId)) {
              scheduleInvalidate();
            }
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'messages' },
        (payload) => {
          const message = payload.new as { sender_id: string; receiver_id: string };
          const otherUserId =
            message.sender_id === currentUserId ? message.receiver_id : message.sender_id;
          if (
            message.sender_id === currentUserId ||
            message.receiver_id === currentUserId
          ) {
            if (!includeSet || includeSet.has(otherUserId)) {
              scheduleInvalidate();
            }
          }
        }
      )
      .subscribe();

    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      void supabase.removeChannel(channel);
    };
  }, [currentUserId, includeUserIds, queryClient]);

  return query;
};
